import { AddRounded } from '@mui/icons-material'
import { Autocomplete, Box, Button, Checkbox, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import Helper from '../../../helpers/Helper'
import FormHelper from './FormHelper'
import { FormBuilderOptions, FormBuilderSCC } from './Interfaces'
import SmartDatePicker from '../../SmartDatePicker'
import { IClassifiedFeature } from '../../../definitions/Classification'
import VectorIcon from '../../icons/VectorIcon'
import InfoSharpIcon from '@mui/icons-material/InfoSharp'
import { snackbar } from '../../services/SnackbarService'
import { IFormField } from '../../../definitions/FormField'
import { IFormGroup } from '../../../definitions/FormGroup'
import ContractsManager from '../../../managers/ContractsManager'

interface P<T extends object> {
    item: any
    field: IFormField
    index: number
    onChange: (item: any) => void
    scc: FormBuilderSCC<T>
    stringOptions: FormBuilderOptions<T>
    step: IFormGroup
    ingestionMode?: boolean
    features?: IClassifiedFeature[]
    path?: string
    isReadOnly: boolean
}

const FieldEditor = <T extends object>({ item, field, index, scc, stringOptions, step, onChange, ingestionMode, features, isReadOnly }: P<T>) => {
    const [matchedFeature, setMatchedFeature] = useState<IClassifiedFeature>(undefined)
    const theme = useTheme()
    const [menuOpen, setMenuOpen] = useState(false)
    const [focused, setFocused] = useState(false)
    const [hoveringAibox, setHoveringAibox] = useState(false)
    const shouldRenderAibox = ingestionMode && matchedFeature && (focused || hoveringAibox) && !menuOpen
    const ref = useRef<HTMLDivElement>(null)
    const fieldWidth = useRef<HTMLDivElement>(null)
    const fieldHeight = useRef<HTMLDivElement>(null)
    const fieldHeightAiRef = useRef<HTMLDivElement>(null)
    const [fieldWidthAI, setWidth] = useState(0)
    const [fieldHeightRendered, setHeight] = useState(0)
    const [hasMenuInteracted, setHasMenuInteracted] = useState(false)
    const currentValue = FormHelper.getValue(item, field.key, index, step)

    const handleReRunAI = () => {
        snackbar.show({ severity: 'warning', text: 'features under development' })
    }
    const handlePopupIndicatorClick = () => {
        if (ingestionMode) {
            setHasMenuInteracted(true)
            setMenuOpen(true)
        }
    }

    useEffect(() => {
        if (!ingestionMode || !field?.key) {
            setMatchedFeature(undefined)
            return
        }

        const fieldKeyToMatch = (field as any).fullKey || field.key
        const keyClean = ContractsManager.removeIndex(fieldKeyToMatch.trim().toLowerCase())

        const candidates = features?.filter(f => {
            const featureKeyClean = ContractsManager.removeIndex(f.name.trim().toLowerCase())
            return featureKeyClean === keyClean && f.value != null && f.value !== ''
        }) ?? []

        const byValue = candidates.find(f =>
            ContractsManager.aiValuesMatch(field.type, currentValue, f.value)
        )

        const matched = byValue ?? candidates[0] ?? undefined

        setMatchedFeature(matched)
    }, [ingestionMode, features, field?.key, (field as any)?.fullKey, currentValue, field.type])

    const getConfidenceColor = (confidence: number) => {
        const red = Math.max(0, 255 - Math.floor(confidence * 255))
        const green = Math.min(255, Math.floor(confidence * 255))
        return `rgb(${red}, ${green}, 50)`
    }

    useEffect(() => {
        setWidth(fieldWidth.current?.clientWidth)
        setHeight(fieldHeight.current?.clientHeight)
    })

    useEffect(() => {
        if (!matchedFeature) return

        if (!ContractsManager.aiValuesMatch(field.type, currentValue, matchedFeature.value)) {
            setMatchedFeature(undefined)
            setHoveringAibox(false)
            setFocused(false)
        }
    }, [currentValue, matchedFeature])

    const renderBoxAI = () => {
        return (
            <Box
                ref={fieldHeightAiRef}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mt: 1,
                    backgroundColor: theme.palette.mode === 'dark' ? '#404040' : theme.palette.background.paper
                }}>
                <Box display='flex' alignItems='center' justifyContent='space-between' gap={1} marginTop={2}>
                    <Box sx={{ width: '100%', height: '4px', backgroundColor: '#F0F0F0', borderRadius: '2px', flex: 1 }}>
                        <Box
                            sx={{
                                width: `${matchedFeature?.confidence ? matchedFeature.confidence * 100 : 100}%`,
                                height: '100%',
                                backgroundColor: getConfidenceColor(matchedFeature?.confidence || 1),
                                borderRadius: '2px',
                                transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant='caption' color='textPrimary'>
                            AI confidence: {matchedFeature?.confidence != null ? `${Math.floor(matchedFeature.confidence * 100)}%` : '100%'}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: theme.palette.background.smartable,
                        borderRadius: '12px',
                        padding: '12px',
                        gap: 4,
                        width: '100%',
                        height: '100%'
                    }}>
                    <Box display='flex' gap={1} alignItems='center' marginBottom={2}>
                        <Typography variant='subtitle2' color='textPrimary'>
                            AI Source
                        </Typography>
                        <Tooltip
                            componentsProps={{
                                popper: {
                                    sx: {
                                        zIndex: 9999
                                    }
                                }
                            }}
                            key='ai_source'
                            title='The unstructured text from which the feature was extracted.'>
                            <InfoSharpIcon />
                        </Tooltip>
                    </Box>
                    <Typography
                        data-copyable
                        variant='body2'
                        color='textPrimary'
                        sx={{
                            userSelect: 'text',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.6,
                            mb: 2
                        }}>
                        {matchedFeature?.source || 'N/N'}
                    </Typography>
                    <Box display='flex' justifyContent='flex-end' alignItems='center'>
                        <Button
                            variant='contained'
                            size='small'
                            onClick={handleReRunAI}
                            sx={{
                                fontWeight: 'bold',
                                borderRadius: '20px',
                                gap: 1,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onMouseDown={e => e.stopPropagation()}>
                            <Box display='flex' alignItems='center'>
                                <VectorIcon sx={{ color: theme.palette.primary, fontSize: 20 }} />
                            </Box>
                            RERUN AI
                        </Button>
                    </Box>
                </Box>

            </Box>
        )
    }

    const renderField = () => {
        function disableScroll(e) {
            e.preventDefault()
        }

        const sccKey = (field as any).fullKey ?? field.key

        switch (field.key) {
            default: {
                if (field.type === 'system-config') {
                    const sc = scc[sccKey] ?? []
                    const current = FormHelper.getValue(item, field.key, index, step)
                    const options = sc.map(x => ({ value: x.uuid, label: x.name }))
                    const { isError, helperText } = FormHelper.validateField(field, current, item, index, step)
                    return (
                        <Autocomplete
                            disabled={isReadOnly}
                            size='small'
                            options={options}
                            value={options.find(x => x?.value === current?.uuid) ?? null}
                            getOptionLabel={option => option.label}
                            open={ingestionMode ? hasMenuInteracted && menuOpen : menuOpen}
                            onClose={() => setMenuOpen(false)}
                            onFocus={() => {
                                setFocused(true)
                                if (!ingestionMode) {
                                    setMenuOpen(true)
                                    setHasMenuInteracted(true)
                                }
                            }}
                            onBlur={() => {
                                setFocused(false)
                                setMenuOpen(false)
                                setHasMenuInteracted(false)
                            }}
                            componentsProps={{
                                popupIndicator: {
                                    onClick: handlePopupIndicatorClick
                                }
                            }}
                            onChange={(e, v) => {
                                e.preventDefault()
                                const newSC = sc.find(x => x.uuid === v?.value) ?? null
                                const res = FormHelper.setValue(item, field.key, newSC, step)
                                onChange(res)
                            }}
                            renderInput={params => (

                                <TextField
                                    {...params}
                                    error={isError}
                                    helperText={helperText ?? ' '}
                                    size='small'
                                    fullWidth
                                    onFocus={e => {
                                        setFocused(true)
                                        e.target.addEventListener('wheel', disableScroll, { passive: false })
                                    }}
                                    onBlur={e => {
                                        setFocused(false)
                                        e.target.removeEventListener('wheel', disableScroll)
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={isReadOnly}
                                />

                            )}
                        />
                    )
                } else if (field.type === 'multi-system-config') {
                    const sc = scc[sccKey] ?? []
                    const current = FormHelper.getValue(item, field.key, index, step)
                    const options = sc.map(x => ({ value: x.uuid, label: x.name }))
                    const fieldValue = options.filter(x => current?.map(k => k.uuid).includes(x.value))
                    const { isError, helperText } = FormHelper.validateField(field, current, item, index, step)
                    return (
                        <Autocomplete
                            disabled={isReadOnly}
                            size='small'
                            multiple
                            disableCloseOnSelect
                            open={ingestionMode ? hasMenuInteracted && menuOpen : menuOpen}
                            onClose={() => setMenuOpen(false)}
                            onFocus={() => {
                                setFocused(true)
                                if (!ingestionMode) {
                                    setMenuOpen(true)
                                    setHasMenuInteracted(true)
                                }
                            }}
                            onBlur={() => {
                                setFocused(false)
                                setMenuOpen(false)
                                setHasMenuInteracted(false)
                            }}
                            componentsProps={{
                                popupIndicator: {
                                    onClick: handlePopupIndicatorClick
                                }
                            }}
                            options={options}
                            value={fieldValue ?? null}
                            getOptionLabel={option => option.label}
                            onChange={(e, v) => {
                                e.preventDefault()
                                const newSC = sc.filter(x => v.map(k => k?.value).includes(x.uuid)) ?? []
                                const res = FormHelper.setValue(item, field.key, newSC, step)
                                onChange(res)
                            }}
                            renderInput={params => {
                                return (
                                    <TextField
                                        {...params}
                                        error={isError}
                                        helperText={helperText ?? ' '}
                                        disabled={isReadOnly}
                                        onFocus={e => {
                                            setFocused(true)
                                            e.target.addEventListener('wheel', disableScroll, { passive: false })
                                        }}
                                        onBlur={e => {
                                            setFocused(false)
                                            e.target.removeEventListener('wheel', disableScroll)
                                        }}
                                        size='small'
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                )
                            }}
                        />

                    )
                } else if (field.type === 'string-options') {
                    const selectedOptions = stringOptions[sccKey] ?? []
                    const current = FormHelper.getValue(item, field.key, index, step)
                    const { isError, helperText } = FormHelper.validateField(field, current, item, index, step)

                    return (
                        <Autocomplete
                            size='small'
                            options={selectedOptions}
                            disabled={isReadOnly}
                            value={current ?? null}
                            getOptionLabel={option => option}
                            onChange={(e, v) => {
                                e.preventDefault()
                                const res = FormHelper.setValue(item, field.key, v ?? null, step) ?? null
                                onChange(res)
                            }}
                            renderInput={params => {
                                return (
                                    <TextField
                                        {...params}
                                        error={isError}
                                        onFocus={e => {
                                            setFocused(true)
                                            e.target.addEventListener('wheel', disableScroll, { passive: false })
                                        }}
                                        disabled={isReadOnly}
                                        onBlur={e => {
                                            setFocused(false)
                                            e.target.removeEventListener('wheel', disableScroll)
                                        }}
                                        helperText={helperText ?? ' '}
                                        size='small'
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                )
                            }}
                        />
                    )
                } else if (field.type === 'list') {
                    // TODO: Temporary fix
                    const rawChildItems = FormHelper.getValue(item, field.key, index, step) ?? []
                    const childItems = Array.isArray(rawChildItems) ? rawChildItems : []
                    const { isError, helperText } = FormHelper.validateField(field, childItems, item, index, step)
                    return (
                        <div style={{ paddingLeft: theme.spacing(3), position: 'relative' }}>
                            <Button
                                disabled={isReadOnly}
                                startIcon={<AddRounded />}
                                size='small'
                                onFocus={e => {
                                    setFocused(true)
                                    e.target.addEventListener('wheel', disableScroll, { passive: false })
                                }}
                                onBlur={e => {
                                    setFocused(false)
                                    e.target.removeEventListener('wheel', disableScroll)
                                }}
                                onClick={() => {
                                    const newListItem = { uuid: Helper.generateUUID() }
                                    field.childFields?.forEach(f => {
                                        const fk = f.key.split('.').at(-1)
                                        newListItem[fk] = null
                                        if (f.type === 'list') newListItem[fk] = []
                                    })

                                    const newList = [...(childItems ?? []), newListItem]
                                    const res = FormHelper.setValue(item, field.key, newList, step)
                                    onChange(res)

                                    setTimeout(() => {
                                        const container = ref.current?.closest('.master-editor-field')
                                        if (!container) return

                                        const allHeaders = container.querySelectorAll('.tree-viewer-header-container')
                                        const lastHeader = allHeaders[allHeaders.length - 1]

                                        if (lastHeader) {
                                            lastHeader.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center'
                                            })
                                        }
                                    }, 50)
                                }}>
                                Add a new {field.label}
                            </Button>
                            {isError &&
                                (
                                    <div style={{ position: 'absolute', bottom: -10, left: 53, margin: 'auto' }}>
                                        <Typography align='center' color='error' fontSize={10}>{helperText ?? ' '}</Typography>
                                    </div>
                                )}
                        </div>
                    )
                } else if (field.type === 'object') {
                    return null
                } else if (field.type === 'bool') {
                    const value = FormHelper.getValue(item, field.key, index, step)
                    const { isError, helperText } = FormHelper.validateField(field, value, item, index, step)
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                disabled={isReadOnly}
                                checked={value ?? null}
                                onFocus={e => {
                                    setFocused(true)
                                    e.target.addEventListener('wheel', disableScroll, { passive: false })
                                }}
                                onBlur={e => {
                                    setFocused(false)
                                    e.target.removeEventListener('wheel', disableScroll)
                                }}
                                onChange={e => {
                                    const checked = e.target.checked
                                    onChange(FormHelper.setValue(item, field.key, checked, step))
                                }}
                            />
                            {isError && <Typography color='error' fontSize={13}>{helperText ?? ' '}</Typography>}
                        </div>
                    )
                } else if (field.type === 'date') {
                    const value = FormHelper.getValue(item, field.key, index, step)
                    const { isError, helperText } = FormHelper.validateField(field, value, item, index, step)

                    return (
                        <>
                            <div
                                onFocus={() => setFocused(true)}
                                onBlur={e => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                        setFocused(false)
                                    }
                                }}
                                style={{ position: 'relative' }}>

                                <SmartDatePicker
                                    disabled={isReadOnly}
                                    value={value}
                                    onChange={newValue => onChange(FormHelper.setValue(item, field.key, newValue, step))}
                                    error={isError}
                                    helperText={helperText ?? ' '}
                                />
                            </div>
                        </>
                    )
                } else {
                    const value = FormHelper.getValue(item, field.key, index, step)
                    const multiline = field.type === 'long-form'
                    const { isError, helperText } = FormHelper.validateField(field, value, item, index, step)

                    return (
                        <TextField
                            disabled={isReadOnly}
                            error={isError}
                            helperText={helperText ?? ' '}
                            value={value}
                            onChange={e => {
                                const v = e.target.value
                                onChange(FormHelper.setValue(item, field.key, v, step))
                            }}
                            onFocus={e => {
                                setFocused(true)
                                e.target.addEventListener('wheel', disableScroll, { passive: false })
                            }}
                            onBlur={e => {
                                setFocused(false)
                                e.target.removeEventListener('wheel', disableScroll)
                            }}
                            size='small'
                            className={theme.palette.mode === 'dark' ? 'white-calendar-picker-indicator' : ''}
                            type={field.type === 'number' ? 'number' : undefined}
                            multiline={multiline}
                            minRows={multiline ? 4 : undefined}
                            maxRows={multiline ? 10 : undefined} // If more than 17, in contracts with long text fields, a ResizeObserver bug may occur.
                            fullWidth
                        />
                    )
                }
            }
        }
    }

    return (
        <div
            ref={ref}
            style={{
                position: 'relative',
                border: shouldRenderAibox ? `1px solid ${theme.palette.divider}` : 'none',
                borderRadius: 'calc(var(--unit) * 1)',
                height: '100%'
            }}
            onBlur={e => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setFocused(false)
                }
            }}>
            <div
                ref={fieldWidth && fieldHeight}
                style={{
                    ...(shouldRenderAibox && {
                        position: 'relative',
                        zIndex: 1800
                    })
                }}>
                {renderField()}
            </div>
            {(shouldRenderAibox && field.type !== 'list') && (
                <>
                    <Box
                        sx={{
                            'position': 'absolute',
                            'top': '-10px',
                            'zIndex': 1600,
                            'right': '-10px',
                            'left': '-10px',
                            'padding': 1,
                            'border': '1px solid divider',
                            'borderRadius': 'calc(var(--unit) * 1)',
                            'animation': '0.2s ease forwards fadeIn',
                            '@keyframes fadeIn': {
                                from: { opacity: 0, transform: 'translateY(-10px)' },
                                to: { opacity: 1, transform: 'translateY(0)' }
                            },
                            'boxShadow': theme.shadows[6],
                            'backgroundColor': theme.palette.mode === 'dark' ? '#404040' : theme.palette.background.paper
                        }}>
                        <div style={{ width: fieldWidthAI, height: fieldHeightRendered, pointerEvents: 'none' }} />

                        <Box
                            tabIndex={-1}
                            onMouseEnter={() => setHoveringAibox(true)}
                            onMouseLeave={() => setHoveringAibox(false)}>
                            {renderBoxAI()}
                        </Box>
                        <Box />
                    </Box>
                </>
            )}
        </div>
    )
}

export default FieldEditor
